import React, { Fragment, useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import theme from "../../styles/theme";
import { useScheduleContext } from "../../contexts/schedule";
import { RaceResultsRow, QualiResultsRow, EmptyRowQ1, EmptyRowQ2 } from "./RaceResultsRow";
import msToTime from "../../utils/helpers/msToTime";
import { getDivisionColor, getDivisionTextColor, getDivisionPriority } from "../../utils/helpers/divisionUtils";

import _ from "lodash";

const StandingsContainer = styled.div`
  position: relative;
  font-family: "Montserrat", sans-serif;
  // padding: 4px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
`;

const StyledTableCell = styled(TableCell)`
  &.${tableCellClasses.head} {
    color: ${theme.colors.grey};
    text-transform: uppercase;
    font-size: 14px;
    font-family: "Montserrat", sans-serif;
    font-weight: bold;
    white-space: nowrap;

    text-align: left;
    border-bottom: none;
  }
`;

const StyledTable = styled(Table)`
  margin: auto;
  min-width: 360px !important;
  background-color: ${theme.colors.dark};
  border-radius: 0px;

  & > tbody > tr > td {
    font-family: "Montserrat", sans-serif;
    font-weight: 600;

    color: ${theme.colors.white};
    border-bottom: none;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  z-index: 3;
`;

const NoData = styled.div`
  text-align: center;
  opacity: 50%;
  text-transform: uppercase;
  margin-top: 30px;
`;

const raceTypes = {
  Qualification: "qualiResults",
  Sprint: "sprintResults",
  Race: "raceResults",
};

export const DivisionBadge = ({ name, color }) => {
  if (!color) {
    return (
      <div
        style={{
          fontSize: "16px",
          fontWeight: "bold",
          color: theme.colors.grey,
          textTransform: "uppercase",
          letterSpacing: "1px",
        }}
      >
        {name}
      </div>
    );
  }

  return (
    <div>
      <span
        style={{
          display: "inline-block",
          backgroundColor: color,
          color: getDivisionTextColor(color),
          padding: "4px 12px",
          borderRadius: "4px",
          fontSize: "16px",
          fontWeight: "bold",
          textTransform: "uppercase",
          letterSpacing: "1px",
        }}
      >
        {name}
      </span>
    </div>
  );
};

const formatQualiResults = (race) => {
  if (!race?.qualiResults) return;

  Object.values(race.qualiResults).forEach((qualiList) => {
    if (!qualiList) return;
    _.forEach(qualiList, (qualiResult) => {
      const { status, fastestLap } = qualiResult;
      const dnf = status === "DNF";
      const dsq = status === "DSQ";
      const hasTime = !dsq && !dnf;

      if (!hasTime) {
        if (dnf) qualiResult.timeText = "DNF";
        if (dsq) qualiResult.timeText = "DSQ";
        return;
      }
      qualiResult.timeText = `${msToTime(fastestLap?.time)}`;
    });
  });
};

export function RaceResultsTable({ race, type, minFastestLapTime, laps }) {
  const { seasons } = useScheduleContext();
  const divisionColors = seasons[race.details?.seasonId]?.divisionColors;
  const resultsMap = race[raceTypes[type]] || {};
  const showDivisionBadge = Object.keys(resultsMap).length > 1;

  return (
    <Fragment>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", paddingTop: showDivisionBadge ? "24px" : "0px" }}>
        {Object.entries(resultsMap)
          .sort(([divA], [divB]) => getDivisionPriority(divisionColors, divA) - getDivisionPriority(divisionColors, divB))
          .map(([divisionName, results]) => {
          if (!results || results.length === 0) return null;
          const firstRow = results[0];
          return (
            <div key={divisionName} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {showDivisionBadge && (
                <DivisionBadge
                  name={divisionName}
                  color={getDivisionColor(divisionColors, divisionName)}
                />
              )}
              <StandingsContainer>
                <StyledTableContainer>
                  <StyledTable sx={{}}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: "10px", padding: "16px 4px 16px 16px" }}>
                          Поз
                        </StyledTableCell>

                        <StyledTableCell
                          style={{ width: "40px", padding: "16px 4px 16px 4px" }}
                        ></StyledTableCell>

                        <StyledTableCell style={{ width: "280px" }}>Пілот</StyledTableCell>
                        <StyledTableCell
                          sx={{
                            "@media (max-width: 480px)": {
                              display: "none",
                            },
                            width: "200px",
                          }}
                        >
                          Команда
                        </StyledTableCell>

                        <StyledTableCell style={{ width: "100px" }}>Час</StyledTableCell>

                        <StyledTableCell style={{ width: "60px" }}>Штраф</StyledTableCell>

                        {firstRow.stints && (
                          <StyledTableCell style={{ width: "280px" }}>Гума</StyledTableCell>
                        )}

                        <StyledTableCell style={{ width: "200px" }}>Найшвидше Коло</StyledTableCell>

                        {firstRow.elo && (
                          <StyledTableCell style={{ width: "40px" }}>Slick Rating</StyledTableCell>
                        )}

                        <StyledTableCell style={{ width: "40px" }}>Очки</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results
                        .filter((row, i) => !(row.status === "DNS" || row.status === "DNP"))
                        .map((row, i) => {
                          return (
                            <RaceResultsRow
                              key={row.number}
                              {...row}
                              isFastestLap={row.fastestLap?.time === minFastestLapTime}
                              laps={laps}
                            />
                          );
                        })}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              </StandingsContainer>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
}

export function QualificationResultsTable({ race }) {
  const { seasons } = useScheduleContext();

  const simulator = seasons[race.details.seasonId]?.simulator;
  const renderStints = simulator !== "iRacing";
  const qualiType = race.details.qualiType;
  const divisionColors = seasons[race.details?.seasonId]?.divisionColors;
  const resultsMap = race[raceTypes["Qualification"]] || {};
  const showDivisionBadge = Object.keys(resultsMap).length > 1;

  return (
    <Fragment>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px", paddingTop: showDivisionBadge ? "24px" : "0px" }}>
        {Object.entries(resultsMap)
          .sort(([divA], [divB]) => getDivisionPriority(divisionColors, divA) - getDivisionPriority(divisionColors, divB))
          .map(([divisionName, results]) => {
          if (!results || results.length === 0) return null;
          return (
            <div key={divisionName} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {showDivisionBadge && (
                <DivisionBadge
                  name={divisionName}
                  color={getDivisionColor(divisionColors, divisionName)}
                />
              )}
              <StandingsContainer>
                <StyledTableContainer>
                  <StyledTable sx={{}}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: "10px", padding: "16px 4px 16px 16px" }}>
                          Поз
                        </StyledTableCell>
                        <StyledTableCell>Пілот</StyledTableCell>
                        <StyledTableCell
                          sx={{
                            "@media (max-width: 480px)": {
                              display: "none",
                            },
                            width: "500px",
                          }}
                        >
                          Команда
                        </StyledTableCell>
                        <StyledTableCell style={{ textAlign: "right" }}></StyledTableCell>
                <StyledTableCell style={{ textAlign: "right", width: "80px" }}>Час</StyledTableCell>
                        {renderStints && <StyledTableCell style={{ width: "280px" }}>Гума</StyledTableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.map((row, i) => {
                        return (
                          <Fragment key={row.number}>
                            {i === 10 && <EmptyRowQ2 qualiType={qualiType} />}
                            {i === 15 && <EmptyRowQ1 qualiType={qualiType} />}
                            <QualiResultsRow {...row} />
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </StyledTable>
                </StyledTableContainer>
              </StandingsContainer>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
}

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonCell = styled.div`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.06) 0%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(255, 255, 255, 0.06) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  height: 18px;
  width: ${props => props.$width || "80%"};
  border-radius: 4px;
`;

export function RaceResultsSkeleton() {
  return (
    <StandingsContainer style={{ marginTop: 24 }}>
      <StyledTableContainer>
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "10px", padding: "16px 4px 16px 16px" }}>Поз</StyledTableCell>
              <StyledTableCell style={{ width: "40px", padding: "16px 4px 16px 4px" }}></StyledTableCell>
              <StyledTableCell style={{ width: "280px" }}>Пілот</StyledTableCell>
              <StyledTableCell
                sx={{
                  "@media (max-width: 480px)": {
                    display: "none",
                  },
                  width: "200px",
                }}
              >
                Команда
              </StyledTableCell>
              <StyledTableCell style={{ width: "100px" }}>Час</StyledTableCell>
              <StyledTableCell style={{ width: "60px" }}>Штраф</StyledTableCell>
              <StyledTableCell style={{ width: "280px" }}>Гума</StyledTableCell>
              <StyledTableCell style={{ width: "200px" }}>Найшвидше Коло</StyledTableCell>
              <StyledTableCell style={{ width: "40px" }}>Slick Rating</StyledTableCell>
              <StyledTableCell style={{ width: "40px" }}>Очки</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell style={{ padding: "16px 4px 16px 16px" }}><SkeletonCell $width="20px" /></TableCell>
                <TableCell style={{ padding: "16px 4px 16px 4px" }}><SkeletonCell $width="24px" /></TableCell>
                <TableCell><SkeletonCell $width="180px" /></TableCell>
                <TableCell
                  sx={{
                    "@media (max-width: 480px)": {
                      display: "none",
                    },
                  }}
                ><SkeletonCell $width="140px" /></TableCell>
                <TableCell><SkeletonCell $width="80px" /></TableCell>
                <TableCell><SkeletonCell $width="30px" /></TableCell>
                <TableCell><SkeletonCell $width="160px" /></TableCell>
                <TableCell><SkeletonCell $width="90px" /></TableCell>
                <TableCell><SkeletonCell $width="40px" /></TableCell>
                <TableCell><SkeletonCell $width="30px" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </StandingsContainer>
  );
}

export function RaceResults({ type, race }) {
  const [laps, setLaps] = useState(0);
  const [minFastestLapTime, setMinFastestLapTime] = useState(Infinity);

  const isQualification = type === "Qualification";

  useEffect(() => {
    formatQualiResults(race);

    if (race) {
      const resultsField = race[raceTypes[type]];
      if (resultsField) {
        const allResults = Object.values(resultsField).flat();
        const fastestLapTimes = allResults.map((result) =>
          result.fastestLap ? result.fastestLap.time : Infinity,
        );
        const minTime = Math.min(...fastestLapTimes);

        setMinFastestLapTime(minTime);
        const firstDivisionResults = Object.values(resultsField)[0];
        setLaps(firstDivisionResults?.[0]?.lapsCount || 0);
      }
    }
  }, [race, type]);

  if (!race) {
    return <RaceResultsSkeleton />;
  }

  if (!race[raceTypes[type]]) {
    return <NoData>No data </NoData>;
  }

  if (isQualification) return <QualificationResultsTable race={race} />;

  const props = { race, minFastestLapTime, type, laps };
  return <RaceResultsTable {...props} />;
}
